"use client";

import { useState, useTransition } from "react";
import { incrementCounter, decrementCounter } from "../actions/counter.js";

interface CounterProps {
  initialCount: number;
}

export function Counter({ initialCount }: CounterProps) {
  const [count, setCount] = useState(initialCount);
  const [isPending, startTransition] = useTransition();

  const handleIncrement = () => {
    startTransition(async () => {
      const newCount = await incrementCounter();
      setCount(newCount);
    });
  };

  const handleDecrement = () => {
    startTransition(async () => {
      const newCount = await decrementCounter();
      setCount(newCount);
    });
  };

  return (
    <div data-testid="counter" className="flex flex-col items-center">
      <div
        className={`text-6xl font-bold tabular-nums mb-6 transition-opacity ${
          isPending ? "opacity-50" : ""
        }`}
        data-testid="counter-value"
      >
        <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
          {count}
        </span>
      </div>
      <div className="flex items-center gap-4">
        <button
          onClick={handleDecrement}
          disabled={isPending}
          data-testid="counter-decrement"
          className="w-14 h-14 text-2xl font-medium rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95 border border-slate-200"
        >
          -
        </button>
        <button
          onClick={handleIncrement}
          disabled={isPending}
          data-testid="counter-increment"
          className="w-14 h-14 text-2xl font-medium rounded-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95 shadow-lg shadow-indigo-200"
        >
          +
        </button>
      </div>
      {isPending && (
        <div
          className="mt-4 flex items-center gap-2 text-sm text-slate-500"
          data-testid="counter-pending"
        >
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Updating...
        </div>
      )}
    </div>
  );
}
